import { is, List } from "immutable";
import * as React from "react";
import { connect } from "react-redux";
import * as redux from "redux";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import * as M from "../../../models";
import { Api } from "../../../services/Api";
import { AppState } from "../../../store";
import { Utility } from "../../../utils";

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    ...Comp.Base.DefaultStatePropsMap(state, ownProps),
    abstracts: state.adminAbstracts.abstracts,
    loadingAbstract: state.adminAbstracts.loadingAbstract,
    loadingType: state.adminAbstracts.loadingType,
    types: state.adminAbstracts.types,
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    ...Comp.Base.DefaultDispatchPropsMap(dispatch),
    loadAbstract: (guid: string) => dispatch(Actions.AdminAbstract.loadAbstract(guid)),
    loadType: (typeId: number) => dispatch(Actions.AdminAbstract.loadType(typeId)),
    subscribeToMediaProcessing: (mediaId: number) => dispatch(Actions.DBSubs.subscribeToMediaProcessing(mediaId)),
    toggleLoadingAbstract: (guid: string, loading: boolean) => dispatch(Actions.AdminAbstract.toggleLoadingAbstract(guid, loading)),
    toggleLoadingType: (typeId: number, loading: boolean) => dispatch(Actions.AdminAbstract.toggleLoadingType(typeId, loading)),
});

interface OwnProps {
    guid: string;
    onDataUpdated: (loading: boolean, abstract?: M.Abstract.Abstract, type?: M.Abstract.AbstractType) => void;
    onErrors: (errors: string[]) => void;
    // onLoadingUpdated: (loading: boolean) => void;
}

type ConnectedState = Comp.Base.DefaultConnectedState & {
    abstracts: List<M.Abstract.Abstract>;
    loadingAbstract: M.Generic.ItemLoadingState;
    loadingType: M.Generic.ItemLoadingState;
    types: List<M.Abstract.AbstractType>;
};

type ConnectedDispatch = Comp.Base.DefaultConnectedDispatch & {
    loadAbstract: (guid: string) => void;
    loadType: (typeId: number) => void;
    subscribeToMediaProcessing: (mediaId: number) => void;
    toggleLoadingAbstract: (guid: string, loading: boolean) => void;
    toggleLoadingType: (typeId: number, loading: boolean) => void;
};

interface OwnState {
}

type CombinedProps = ConnectedState & ConnectedDispatch & OwnProps;

class LoaderComponent extends React.Component<CombinedProps, OwnState> {

    private _abstractTypeId = 0;
    private _isMounted = false;

    public constructor(props: any) {
        super(props);
        this.state = {
        };
    }

    public componentDidMount() {
        this._isMounted = true;
        this.initializeComponent();
    }

    public componentDidUpdate(prevProps: Readonly<CombinedProps>) {
        if (!is(prevProps.abstracts, this.props.abstracts))
            this.loadDataFromState();
        if (!is(prevProps.loadingAbstract, this.props.loadingAbstract))
            this.loadDataFromState();
        if (!is(prevProps.loadingAbstract, this.props.loadingAbstract) || !is(prevProps.loadingType, this.props.loadingType))
            this.loadDataFromState();
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public render() {
        return null;
    }

    private async initializeComponent() {

        // first get type ID by abstract GUID
        const guid = this.props.guid;

        // first check if we can get abstract type ID from state
        const _abstract = this.props.abstracts.find((x: M.Abstract.Abstract) => x.guid.toLowerCase() === guid.toLowerCase());
        if (_abstract) {
            this._abstractTypeId = _abstract.typeId;
            this.props.loadType(this._abstractTypeId);
        }
        else {
            // if not, get it from api
            this.props.onDataUpdated(true, _abstract, undefined);
            const typeIdResult = await this.props.api.get(Api.endpoints.abstract.typeIdByAbstractGuid.withUrlParams({ guid }));

            if (!this._isMounted)
                return;

            this.props.onDataUpdated(false, _abstract, undefined);

            if (!typeIdResult.isSuccess) {
                this.props.onErrors(M.Result.Result.errorsOrMessage(typeIdResult));
                return;
            }

            this._abstractTypeId = typeIdResult.data || 0;
            this.props.loadType(this._abstractTypeId);
        }

        this.loadDataFromState(true);
        this.props.loadAbstract(guid);
    }

    private async listenForMediaNotifications(abstract: M.Abstract.Abstract) {
        for (const m of abstract.media.filter((x) => x.isProcessing)) {
            this.props.subscribeToMediaProcessing(m.id);
            await Utility.delay(125);
        }
    }

    private async loadDataFromState(isInitial?: boolean) {
        const guid = this.props.guid;
        let abstract: M.Abstract.Abstract | undefined;
        let type: M.Abstract.AbstractType | undefined;
        const loading = !!this.props.loadingType[this._abstractTypeId] || !!this.props.loadAbstract[guid];

        if (this.props.abstracts.size)
            abstract = this.props.abstracts.find((x: M.Abstract.Abstract) => x.guid.toLowerCase() === guid.toLowerCase());

        if (this.props.types.size)
            type = this.props.types.find((x: M.Abstract.AbstractType) => x.id === this._abstractTypeId);

        if (abstract && type) {
            this.listenForMediaNotifications(abstract);
            this.props.onDataUpdated(false, abstract, type);
        }
        else
            this.props.onDataUpdated(loading, abstract, type);

        // toggle loading of data after state set
        if (isInitial) {
            if (!abstract)
                this.props.toggleLoadingAbstract(guid, true);
            if (!type)
                this.props.toggleLoadingType(this._abstractTypeId, true);
        }
    }
}

export const AdminAbstractAndTypeLoader: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(LoaderComponent);



// WEBPACK FOOTER //
// ./src/containers/abstracts/components/AdminAbstractAndTypeLoader.tsx